import { useEffect, useRef, useState } from "react";
import {
  AlignLeft,
  Check,
  ListChecks,
  Pencil,
  Shield,
  Trash2,
  Workflow,
  X,
} from "lucide-react";
import type {
  RoadmapItemCreate,
  RoadmapItemStatus,
  RoadmapTopic,
} from "../types";
import { roadmapStatuses } from "../utils/roadmapStatus";
import RoadmapVoting from "./RoadmapVoting";
import RoadmapComments from "./RoadmapComments";

interface RoadmapItemEditorProps {
  itemId: string | undefined;
  teamOwnerEmail: string;
  initial: RoadmapItemCreate;
  savedDraft: RoadmapItemCreate | null;
  editing: boolean;
  readOnly: boolean;
  topics: RoadmapTopic[];
  busy: boolean;
  onSave: (payload: RoadmapItemCreate) => void;
  onSaveTitle: (title: string) => void;
  onToggleCriterion: (id: string, completed: boolean) => void;
  onClose: () => void;
  onDelete: (() => void) | undefined;
  createdByEmail: string | undefined;
  createdByName: string | undefined;
  createdAt: string | undefined;
}

export default function RoadmapItemEditor({
  itemId,
  teamOwnerEmail,
  initial,
  savedDraft,
  editing,
  readOnly,
  topics,
  busy,
  onSave,
  onSaveTitle,
  onToggleCriterion,
  onClose,
  onDelete,
  createdByEmail,
  createdByName,
  createdAt,
}: RoadmapItemEditorProps) {
  const [draft, setDraft] = useState({
    ...initial,
    description: initial.description ?? "",
    dependencies: initial.dependencies ?? "",
    acceptanceCriteria: initial.acceptanceCriteria ?? [],
    status: initial.status ?? "backlog",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [editingTitle, setEditingTitle] = useState(false);
  const [newCriterion, setNewCriterion] = useState("");
  useEffect(() => {
    if (savedDraft)
      setDraft({
        ...savedDraft,
        description: savedDraft.description ?? "",
        dependencies: savedDraft.dependencies ?? "",
        acceptanceCriteria: savedDraft.acceptanceCriteria ?? [],
        status: savedDraft.status ?? "backlog",
      });
  }, [savedDraft]);
  useEffect(() => {
    setDraft((current) => ({
      ...current,
      acceptanceCriteria: current.acceptanceCriteria.map((criterion) => {
        const saved = initial.acceptanceCriteria?.find(
          (candidate) => candidate.id === criterion.id,
        );
        return saved ? { ...criterion, completed: saved.completed } : criterion;
      }),
    }));
  }, [initial.acceptanceCriteria]);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const editTitleRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    setDraft((current) => ({ ...current, title: initial.title }));
    setErrors((current) => ({ ...current, title: "" }));
    setEditingTitle(false);
  }, [initial.title]);
  const cancelTitle = () => {
    setDraft((current) => ({ ...current, title: initial.title }));
    setErrors((current) => ({ ...current, title: "" }));
    setEditingTitle(false);
    requestAnimationFrame(() => editTitleRef.current?.focus());
  };
  const saveTitle = () => {
    if (busy) return;
    const value = draft.title.trim();
    if (!value || Array.from(value).length > 200) {
      setErrors((current) => ({
        ...current,
        title: "Enter a title of 1-200 characters.",
      }));
      return;
    }
    if (value === initial.title) cancelTitle();
    else onSaveTitle(value);
  };
  useEffect(() => {
    if (editingTitle) titleInputRef.current?.focus();
  }, [editingTitle]);
  const dialogRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const previous = document.activeElement;
    dialogRef.current?.querySelector<HTMLElement>("input, button")?.focus();
    return () => {
      if (previous instanceof HTMLElement || previous instanceof SVGElement)
        previous.focus();
    };
  }, []);
  const title = editing ? initial.title : "Add a roadmap item";
  const fieldLabelClass = `text-sm text-gray-900 dark:text-gray-100 ${editing ? "font-medium" : "font-normal"}`;
  const sectionLabelClass = `flex items-center gap-1.5 text-sm text-gray-900 dark:text-gray-100 ${editing ? "font-semibold" : "font-normal"}`;
  const fieldError = (field: string) =>
    errors[field] ? (
      <p
        id={`roadmap-error-${field}`}
        role="alert"
        className="mt-1 text-sm text-red-600 dark:text-red-400"
      >
        {errors[field]}
      </p>
    ) : null;
  const categoryField = (
    <div>
      <label className={`block ${fieldLabelClass}`}>
        Category{" "}
        <span aria-hidden="true" className="text-red-500 dark:text-red-400">
          *
        </span>
        <select
          required
          className="input mt-1 font-normal text-gray-700 dark:text-gray-300"
          value={draft.topicId}
          disabled={busy || readOnly}
          aria-invalid={Boolean(errors.topicId)}
          aria-describedby={
            errors.topicId ? "roadmap-error-topicId" : undefined
          }
          onChange={(event) =>
            setDraft({ ...draft, topicId: event.target.value })
          }
        >
          <option value="">Select category</option>
          {topics.map((topic) => (
            <option key={topic.id} value={topic.id}>
              {topic.name}
            </option>
          ))}
        </select>
      </label>
      {fieldError("topicId")}
    </div>
  );
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="roadmap-editor-title"
        tabIndex={-1}
        className="mx-4 max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white dark:bg-gray-800"
        onKeyDown={(event) => {
          if (event.key === "Escape" && !busy) {
            event.preventDefault();
            event.stopPropagation();
            onClose();
          }
          if (event.key === "Tab") {
            const controls = dialogRef.current?.querySelectorAll<HTMLElement>(
              "button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled)",
            );
            const first = controls?.[0],
              last = controls?.[controls.length - 1];
            if (event.shiftKey && document.activeElement === first) {
              event.preventDefault();
              last?.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault();
              first?.focus();
            } else if (!first) {
              event.preventDefault();
              dialogRef.current?.focus();
            }
          }
        }}
      >
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-gray-200 bg-white px-6 py-4 dark:border-gray-700 dark:bg-gray-800">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2
                id="roadmap-editor-title"
                className={
                  editingTitle
                    ? "sr-only"
                    : "break-words text-lg font-semibold text-gray-900 dark:text-gray-100"
                }
              >
                {title}
              </h2>
              {editingTitle ? (
                <>
                  <input
                    ref={titleInputRef}
                    aria-label="Project title"
                    className="input font-normal text-gray-700 dark:text-gray-300"
                    value={draft.title}
                    maxLength={200}
                    disabled={busy}
                    aria-invalid={Boolean(errors.title)}
                    aria-describedby={
                      errors.title ? "roadmap-error-title" : undefined
                    }
                    onChange={(event) =>
                      setDraft({ ...draft, title: event.target.value })
                    }
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === "Escape") {
                        event.preventDefault();
                        event.stopPropagation();
                        if (!busy) {
                          if (event.key === "Enter") saveTitle();
                          else cancelTitle();
                        }
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="btn-icon shrink-0"
                    aria-label="Save project title"
                    disabled={busy}
                    onClick={saveTitle}
                  >
                    <Check className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    className="btn-icon shrink-0"
                    aria-label="Cancel title edit"
                    disabled={busy}
                    onClick={cancelTitle}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </>
              ) : (
                editing &&
                !readOnly && (
                  <button
                    ref={editTitleRef}
                    type="button"
                    className="btn-icon shrink-0"
                    aria-label="Edit project title"
                    disabled={busy}
                    onClick={() => setEditingTitle(true)}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                )
              )}
            </div>
            {editingTitle && fieldError("title")}
            {editing && (
              <span
                className={`mt-2 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${roadmapStatuses[initial.status ?? "backlog"].badge}`}
              >
                {roadmapStatuses[initial.status ?? "backlog"].label}
              </span>
            )}
            {!editing && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Add a roadmap item to your team's roadmap. Choose a category and
                proposed dates to display when the project is planned.
              </p>
            )}
            {editing && createdByEmail && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {createdByName?.trim() || createdByEmail}
                {createdAt && (
                  <>
                    {" "}
                    ·{" "}
                    <time dateTime={createdAt}>
                      {new Date(createdAt).toLocaleString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </time>
                  </>
                )}
                {" · "}
                {topics.find((topic) => topic.id === initial.topicId)?.name}
              </p>
            )}
            {editing && itemId && (
              <RoadmapVoting itemId={itemId} disabled={busy} />
            )}
          </div>
          <button
            type="button"
            aria-label="Close item dialog"
            className="btn-icon"
            disabled={busy}
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <form
          id="roadmap-item-form"
          noValidate
          className="space-y-5 px-6 py-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (readOnly || busy || editingTitle) return;
            const next: Record<string, string> = {};
            if (
              !draft.title.trim() ||
              Array.from(draft.title.trim()).length > 200
            )
              next.title = "Enter a title of 1-200 characters.";
            if (Array.from(draft.description).length > 10000)
              next.description =
                "Description must be at most 10000 characters.";
            if (Array.from(draft.dependencies).length > 10000)
              next.dependencies =
                "Dependencies must be at most 10000 characters.";
            if (newCriterion.trim())
              next.acceptanceCriteria =
                "Add the criterion to the checklist before saving.";
            else if (
              draft.acceptanceCriteria.length > 100 ||
              draft.acceptanceCriteria.some(
                (criterion) =>
                  !criterion.text.trim() ||
                  Array.from(criterion.text.trim()).length > 1000,
              )
            )
              next.acceptanceCriteria =
                "Use up to 100 criteria of 1-1000 characters each.";
            if (!topics.some((topic) => topic.id === draft.topicId))
              next.topicId = "Select a category from this team.";
            for (const field of ["startDate", "endDate"] as const) {
              const value = draft[field];
              const date = new Date(`${value}T00:00:00Z`);
              if (
                !/^\d{4}-\d{2}-\d{2}$/.test(value) ||
                value.startsWith("0000") ||
                !Number.isFinite(date.getTime()) ||
                date.toISOString().slice(0, 10) !== value
              )
                next[field] =
                  field === "startDate"
                    ? "Enter a valid start date."
                    : "Enter a valid end date.";
            }
            if (
              !next.startDate &&
              !next.endDate &&
              draft.endDate < draft.startDate
            )
              next.endDate = "End date must be on or after the start date.";
            setErrors(next);
            if (!Object.keys(next).length)
              onSave({
                ...draft,
                title: draft.title.trim(),
                description: draft.description.trim(),
              });
          }}
        >
          {!editing && (
            <div>
              <label className={`block ${fieldLabelClass}`}>
                Title{" "}
                <span
                  aria-hidden="true"
                  className="text-red-500 dark:text-red-400"
                >
                  *
                </span>
                <input
                  required
                  className="input mt-1 font-normal text-gray-700 dark:text-gray-300"
                  value={draft.title}
                  maxLength={200}
                  disabled={busy || readOnly}
                  aria-invalid={Boolean(errors.title)}
                  aria-describedby={
                    errors.title
                      ? "roadmap-title-hint roadmap-error-title"
                      : "roadmap-title-hint"
                  }
                  onChange={(event) =>
                    setDraft({ ...draft, title: event.target.value })
                  }
                />
              </label>
              {fieldError("title")}
              <p
                id="roadmap-title-hint"
                className="mt-1 text-sm text-gray-500 dark:text-gray-400"
              >
                Use a short, clear name that describes the project's goal.
              </p>
            </div>
          )}
          {!editing && categoryField}
          <div>
            <label className={`block ${fieldLabelClass}`}>
              {editing ? (
                <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  <AlignLeft className="h-4 w-4" aria-hidden="true" />
                  Description
                </span>
              ) : (
                "Description"
              )}
              <textarea
                className="input mt-1 h-auto min-h-20 resize-none font-normal text-gray-700 dark:text-gray-300 [field-sizing:content]"
                rows={3}
                placeholder="Describe the project, why it matters, and what you plan to deliver."
                value={draft.description}
                maxLength={10000}
                disabled={busy || readOnly}
                aria-invalid={Boolean(errors.description)}
                aria-describedby={
                  errors.description ? "roadmap-error-description" : undefined
                }
                onChange={(event) =>
                  setDraft({ ...draft, description: event.target.value })
                }
              />
            </label>
            {fieldError("description")}
          </div>
          <fieldset className="space-y-2">
            <legend className={sectionLabelClass}>
              {editing && <ListChecks className="h-4 w-4" aria-hidden="true" />}
              Acceptance Criteria
            </legend>
            {draft.acceptanceCriteria.map((criterion, index) => (
              <div
                key={criterion.id || `new-${index}`}
                className="flex items-start gap-2"
              >
                <input
                  type="checkbox"
                  className="mt-2"
                  aria-label={criterion.text}
                  checked={criterion.completed}
                  disabled={busy || readOnly}
                  onChange={(event) => {
                    if (
                      editing &&
                      initial.acceptanceCriteria?.some(
                        (saved) => saved.id === criterion.id,
                      )
                    )
                      onToggleCriterion(criterion.id, event.target.checked);
                    else
                      setDraft({
                        ...draft,
                        acceptanceCriteria: draft.acceptanceCriteria.map(
                          (current, i) =>
                            i === index
                              ? { ...current, completed: event.target.checked }
                              : current,
                        ),
                      });
                  }}
                />
                <textarea
                  aria-label={`Acceptance criterion ${index + 1}`}
                  className="input flex-1 font-normal text-gray-700 dark:text-gray-300"
                  rows={2}
                  maxLength={1000}
                  value={criterion.text}
                  disabled={busy || readOnly}
                  onChange={(event) =>
                    setDraft({
                      ...draft,
                      acceptanceCriteria: draft.acceptanceCriteria.map(
                        (current, i) =>
                          i === index
                            ? { ...current, text: event.target.value }
                            : current,
                      ),
                    })
                  }
                />
                {!readOnly && (
                  <button
                    type="button"
                    className="btn-icon"
                    aria-label={`Remove acceptance criterion ${index + 1}`}
                    disabled={busy}
                    onClick={() =>
                      setDraft({
                        ...draft,
                        acceptanceCriteria: draft.acceptanceCriteria.filter(
                          (_, i) => i !== index,
                        ),
                      })
                    }
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            {!draft.acceptanceCriteria.length && readOnly && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No acceptance criteria.
              </p>
            )}
            {!readOnly && (
              <div className="flex items-start gap-2">
                <textarea
                  className="input flex-1 font-normal text-gray-700 dark:text-gray-300"
                  aria-label="New acceptance criterion"
                  placeholder="Describe a criterion, then add it to the checklist."
                  rows={2}
                  maxLength={1000}
                  disabled={busy || draft.acceptanceCriteria.length >= 100}
                  value={newCriterion}
                  onChange={(event) => setNewCriterion(event.target.value)}
                />
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={
                    busy ||
                    !newCriterion.trim() ||
                    draft.acceptanceCriteria.length >= 100
                  }
                  onClick={() => {
                    setDraft({
                      ...draft,
                      acceptanceCriteria: [
                        ...draft.acceptanceCriteria,
                        { id: "", text: newCriterion.trim(), completed: false },
                      ],
                    });
                    setNewCriterion("");
                    setErrors((current) => ({
                      ...current,
                      acceptanceCriteria: "",
                    }));
                  }}
                >
                  Add
                </button>
              </div>
            )}
            {fieldError("acceptanceCriteria")}
          </fieldset>
          <div>
            <label className={`block ${fieldLabelClass}`}>
              <span className={sectionLabelClass}>
                {editing && <Workflow className="h-4 w-4" aria-hidden="true" />}
                Dependencies
              </span>
              <textarea
                className="input mt-1 h-auto min-h-16 font-normal text-gray-700 dark:text-gray-300 [field-sizing:content]"
                rows={2}
                maxLength={10000}
                value={draft.dependencies}
                disabled={busy || readOnly}
                onChange={(event) =>
                  setDraft({ ...draft, dependencies: event.target.value })
                }
              />
            </label>
            {fieldError("dependencies")}
          </div>
          {editing && itemId && (
            <RoadmapComments
              itemId={itemId}
              teamOwnerEmail={teamOwnerEmail}
              disabled={busy}
            />
          )}
          {!readOnly && (
            <section
              className="space-y-4"
              aria-label={editing ? "Admin" : undefined}
            >
              {editing && (
                <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  <Shield className="h-4 w-4" aria-hidden="true" />
                  Admin
                </h3>
              )}
              {editing && (
                <div className="grid gap-3 sm:grid-cols-2">
                  {categoryField}
                  {editing && (
                    <label className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                      Status
                      <select
                        className="input mt-1 font-normal text-gray-700 dark:text-gray-300"
                        value={draft.status}
                        disabled={busy || readOnly}
                        onChange={(event) =>
                          setDraft({
                            ...draft,
                            status: event.target.value as RoadmapItemStatus,
                          })
                        }
                      >
                        {Object.entries(roadmapStatuses).map(
                          ([value, status]) => (
                            <option key={value} value={value}>
                              {status.label}
                            </option>
                          ),
                        )}
                      </select>
                    </label>
                  )}
                </div>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className={fieldLabelClass}>
                    Proposed start date{" "}
                    <span
                      aria-hidden="true"
                      className="text-red-500 dark:text-red-400"
                    >
                      *
                    </span>
                    <input
                      type="date"
                      required
                      className="input mt-1 font-normal text-gray-700 dark:text-gray-300"
                      min="0001-01-01"
                      max="9999-12-31"
                      value={draft.startDate}
                      disabled={busy || readOnly}
                      aria-invalid={Boolean(errors.startDate)}
                      aria-describedby={
                        errors.startDate ? "roadmap-error-startDate" : undefined
                      }
                      onChange={(event) =>
                        setDraft({ ...draft, startDate: event.target.value })
                      }
                    />
                  </label>
                  {fieldError("startDate")}
                </div>
                <div>
                  <label className={fieldLabelClass}>
                    Proposed end date{" "}
                    <span
                      aria-hidden="true"
                      className="text-red-500 dark:text-red-400"
                    >
                      *
                    </span>
                    <input
                      type="date"
                      required
                      className="input mt-1 font-normal text-gray-700 dark:text-gray-300"
                      min={draft.startDate || "0001-01-01"}
                      max="9999-12-31"
                      value={draft.endDate}
                      disabled={busy || readOnly}
                      aria-invalid={Boolean(errors.endDate)}
                      aria-describedby={
                        errors.endDate ? "roadmap-error-endDate" : undefined
                      }
                      onChange={(event) =>
                        setDraft({ ...draft, endDate: event.target.value })
                      }
                    />
                  </label>
                  {fieldError("endDate")}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 border-t border-gray-200 pt-4 dark:border-gray-700">
                {!readOnly && (
                  <button
                    className="btn btn-primary"
                    disabled={busy || editingTitle}
                  >
                    {editing ? "Save changes" : "Create item"}
                  </button>
                )}
                {editing && !readOnly && onDelete && (
                  <button
                    type="button"
                    className="btn btn-outline ml-auto gap-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                    disabled={busy}
                    onClick={onDelete}
                  >
                    <Trash2 className="h-4 w-4" /> Delete item
                  </button>
                )}
              </div>
            </section>
          )}
        </form>
      </div>
    </div>
  );
}
